import React, { useState } from 'react';
import cc from 'classcat';
import AnimateHeight from 'react-animate-height';

import FilterIcon from '../../images/icons/new-caret-down.svg';

import styles from './styles.module.scss';

export type SortOption = 'featured' | 'price-asc' | 'price-desc';

export interface UnitFilterState {
  priceMin: number;
  priceMax: number;
  beds: number[];
  baths: number[];
  carParks: number[];
  sortBy: SortOption;
}

interface Props {
  onFilterChange: (filters: UnitFilterState) => void;
  resultCount: number;
  totalCount: number;
  minPrice: number;
  maxPrice: number;
  availableBeds: number[];
  availableBaths: number[];
  availableCarParks: number[];
}

const UnitFilters = ({
  onFilterChange,
  resultCount,
  totalCount,
  minPrice,
  maxPrice,
  availableBeds,
  availableBaths,
  availableCarParks,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<UnitFilterState>({
    priceMin: minPrice,
    priceMax: maxPrice,
    beds: [],
    baths: [],
    carParks: [],
    sortBy: 'featured',
  });

  const updateFilters = (updates: Partial<UnitFilterState>) => {
    const newFilters = { ...filters, ...updates };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFilters({ sortBy: e.target.value as SortOption });
  };

  const handleNumberToggle = (category: 'beds' | 'baths' | 'carParks', value: number) => {
    const current = filters[category];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateFilters({ [category]: updated });
  };

  const handlePriceMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    // If min is dragged past max, swap them
    if (value > filters.priceMax) {
      updateFilters({ priceMin: filters.priceMax, priceMax: value });
    } else {
      updateFilters({ priceMin: value });
    }
  };

  const handlePriceMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    // If max is dragged below min, swap them
    if (value < filters.priceMin) {
      updateFilters({ priceMin: value, priceMax: filters.priceMin });
    } else {
      updateFilters({ priceMax: value });
    }
  };

  const clearPrice = () => updateFilters({ priceMin: minPrice, priceMax: maxPrice });
  const clearBeds = () => updateFilters({ beds: [] });
  const clearBaths = () => updateFilters({ baths: [] });
  const clearCarParks = () => updateFilters({ carParks: [] });

  const clearAll = () => {
    const cleared: UnitFilterState = {
      priceMin: minPrice,
      priceMax: maxPrice,
      beds: [],
      baths: [],
      carParks: [],
      sortBy: 'featured',
    };
    setFilters(cleared);
    onFilterChange(cleared);
    setIsOpen(false);
  };

  const hasActiveFilters =
    filters.priceMin !== minPrice ||
    filters.priceMax !== maxPrice ||
    filters.beds.length > 0 ||
    filters.baths.length > 0 ||
    filters.carParks.length > 0;

  return (
    <div className={styles.unitFilters}>
      <div className={styles.topBar}>
        <button
          type="button"
          className={cc([styles.filterButton, { [styles.active]: isOpen }])}
          onClick={() => setIsOpen(!isOpen)}
        >
          <FilterIcon />
          Filters
        </button>

        <button
          type="button"
          className={styles.clearFiltersButton}
          onClick={clearAll}
          title="Clear all filters"
          disabled={!hasActiveFilters}
        >
          ✕
        </button>

        <div className={styles.resultCount}>
          Showing {resultCount} of {totalCount}
        </div>

        <div className={styles.sortBy}>
          Sort by{' '}
          <select value={filters.sortBy} onChange={handleSortChange}>
            <option value="featured">Featured</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      <AnimateHeight duration={300} height={isOpen ? 'auto' : 0}>
        <div className={styles.filterPanel}>
          <h3 className={styles.filterTitle}>Filters</h3>

          {/* Price Range Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterHeader}>
              <h4>Price Range</h4>
              {(filters.priceMin !== minPrice || filters.priceMax !== maxPrice) && (
                <button type="button" className={styles.clearButton} onClick={clearPrice}>
                  Clear
                </button>
              )}
            </div>
            <div className={styles.priceRange}>
              <div
                className={styles.rangeSliders}
                style={{
                  '--range-min': `${((filters.priceMin - minPrice) / (maxPrice - minPrice)) * 100}%`,
                  '--range-max': `${100 - ((filters.priceMax - minPrice) / (maxPrice - minPrice)) * 100}%`,
                } as React.CSSProperties}
              >
                <input
                  type="range"
                  min={minPrice}
                  max={maxPrice}
                  value={filters.priceMin}
                  onChange={handlePriceMinChange}
                  className={styles.rangeMin}
                />
                <input
                  type="range"
                  min={minPrice}
                  max={maxPrice}
                  value={filters.priceMax}
                  onChange={handlePriceMaxChange}
                  className={styles.rangeMax}
                />
              </div>
              <div className={styles.priceLabels}>
                <span>${filters.priceMin.toLocaleString()}</span>
                <span>${filters.priceMax.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Beds Filter */}
          {availableBeds.length > 0 && (
            <div className={styles.filterSection}>
              <div className={styles.filterHeader}>
                <h4>Bedrooms</h4>
                {filters.beds.length > 0 && (
                  <button type="button" className={styles.clearButton} onClick={clearBeds}>
                    Clear
                  </button>
                )}
              </div>
              <div className={styles.buttonGroup}>
                {availableBeds.map((num) => (
                  <button
                    key={num}
                    type="button"
                    className={cc([styles.toggleButton, { [styles.active]: filters.beds.includes(num) }])}
                    onClick={() => handleNumberToggle('beds', num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Baths Filter */}
          {availableBaths.length > 0 && (
            <div className={styles.filterSection}>
              <div className={styles.filterHeader}>
                <h4>Bathrooms</h4>
                {filters.baths.length > 0 && (
                  <button type="button" className={styles.clearButton} onClick={clearBaths}>
                    Clear
                  </button>
                )}
              </div>
              <div className={styles.buttonGroup}>
                {availableBaths.map((num) => (
                  <button
                    key={num}
                    type="button"
                    className={cc([styles.toggleButton, { [styles.active]: filters.baths.includes(num) }])}
                    onClick={() => handleNumberToggle('baths', num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Car Parks Filter */}
          {availableCarParks.length > 0 && (
            <div className={styles.filterSection}>
              <div className={styles.filterHeader}>
                <h4>Car Parks</h4>
                {filters.carParks.length > 0 && (
                  <button type="button" className={styles.clearButton} onClick={clearCarParks}>
                    Clear
                  </button>
                )}
              </div>
              <div className={styles.buttonGroup}>
                {availableCarParks.map((num) => (
                  <button
                    key={num}
                    type="button"
                    className={cc([styles.toggleButton, { [styles.active]: filters.carParks.includes(num) }])}
                    onClick={() => handleNumberToggle('carParks', num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.filterActions}>
            <button type="button" className={styles.clearAllButton} onClick={clearAll}>
              Clear all
            </button>
            <button type="button" className={styles.applyButton} onClick={() => setIsOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </AnimateHeight>
    </div>
  );
};

export default UnitFilters;

